import {
  Add as AddIcon,
  Category as CategoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useProgramCategories, useResourceCategories } from "../../hooks/useRedux";
import { Category } from "../../store/slices/types";

// Helper function to format date
const formatDate = (date: number | string | Date | undefined): string => {
  if (!date) return "N/A";
  const dateObj = typeof date === 'number' ? new Date(date) : new Date(date);
  if (isNaN(dateObj.getTime())) return "N/A";
  return dateObj.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Categories: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isProgramCategories = location.pathname === "/program-categories";
  const isResourceCategories = location.pathname === "/resource-categories";
  const categoryType: "program" | "resource" = isProgramCategories
    ? "program"
    : isResourceCategories
    ? "resource"
    : "program";

  // Use appropriate hook based on route
  const programCategoriesHook = useProgramCategories();
  const resourceCategoriesHook = useResourceCategories();

  const {
    categories,
    loading,
    error,
    deleteCategory,
  } = isProgramCategories ? programCategoriesHook : resourceCategoriesHook;
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // Categories are already filtered by the hook (useProgramCategories or useResourceCategories)
  // No need to filter by type since we have separate hooks for each type
  const filteredCategories = categories;

  // Calculate statistics for the current category type
  const totalCategories = filteredCategories.length;
  const activeCategories = filteredCategories.filter((c) => c.isActive).length;
  const inactiveCategories = filteredCategories.filter(
    (c) => !c.isActive
  ).length;

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      renderCell: (params) => (
        <Box>
          <Typography variant='body2' fontWeight='600'>
            {params.value}
          </Typography>
          <Typography variant='caption' color='textSecondary' noWrap>
            {params.row.description?.substring(0, 50) || "No description"}...
          </Typography>
        </Box>
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      flex: 0.4,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          size='small'
          color={params.value ? "success" : "default"}
          variant='outlined'
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2' color='text.secondary'>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      flex: 0.8,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <Typography variant='body2' color='text.secondary'>
          {formatDate(params.value)}
        </Typography>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.3,
      minWidth: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={
            <Tooltip title='Edit Category'>
              <EditIcon />
            </Tooltip>
          }
          label='Edit'
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title='Delete Category'>
              <DeleteIcon />
            </Tooltip>
          }
          label='Delete'
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  const handleAdd = () => {
    const basePath = isProgramCategories
      ? "/program-categories"
      : "/resource-categories";
    navigate(`${basePath}/add`);
  };

  const handleEdit = (category: Category) => {
    const id = category._id || category.id;
    if (id) {
      const basePath = isProgramCategories
        ? "/program-categories"
        : "/resource-categories";
      navigate(`${basePath}/edit/${id}`);
    }
  };

  const handleDelete = async (id: string) => {
    const category = categories.find((c) => (c._id || c.id) === id);
    const categoryName = category?.name || "Category";

    if (
      window.confirm(
        `Are you sure you want to delete "${categoryName}"? This action cannot be undone.`
      )
    ) {
      try {
        await deleteCategory(id);
        toast.success("category is deleted");
      } catch (error) {
        console.error("Failed to delete category:", error);
        toast.error(
          `❌ Failed to delete category: ${
            error instanceof Error ? error.message : "Unknown error"
          }`
        );
      }
    }
  };


  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant='h4' fontWeight='700' gutterBottom>
              {categoryType === "program" ? "Program" : "Resource"} Category
              Management
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Create and manage categories for{" "}
              {categoryType === "program" ? "programs" : "resources"}
            </Typography>
          </Box>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
              },
            }}
          >
            Create Category
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: 2,
                transition: "all 0.3s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography variant='h4' fontWeight='bold'>
                      {totalCategories}
                    </Typography>
                    <Typography variant='body2' sx={{ opacity: 0.9 }}>
                      Total{" "}
                      {categoryType === "program" ? "Program" : "Resource"}{" "}
                      Categories
                    </Typography>
                  </Box>
                  <CategoryIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                transition: "all 0.3s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                border: "2px solid #4caf50",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(76, 175, 80, 0.3)",
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='success.main'
                    >
                      {activeCategories}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Active Categories
                    </Typography>
                  </Box>
                  <CategoryIcon sx={{ fontSize: 40, color: "success.main" }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                transition: "all 0.3s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                border: "2px solid #9e9e9e",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 25px rgba(158, 158, 158, 0.3)",
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      color='text.secondary'
                    >
                      {inactiveCategories}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Inactive Categories
                    </Typography>
                  </Box>
                  <CategoryIcon
                    sx={{ fontSize: 40, color: "text.secondary" }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Data Table */}
      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={filteredCategories}
          columns={columns}
          getRowId={(row) => row._id || row.id || String(Math.random())}
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={filteredCategories.length}
          loading={loading}
          autoHeight
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: alpha("#667eea", 0.1),
              borderBottom: `2px solid ${alpha("#667eea", 0.2)}`,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha("#667eea", 0.05),
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default Categories;
